import { useCallback, useMemo, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Combobox,
  EmptyState,
  Field,
  Input,
  MultiCombobox,
  Panel,
  SegmentedControl,
  type MultiComboboxOption,
  type SegmentedControlOption,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { DashboardFilterBar, FilterSlot } from '@/components/dashboards/DashboardFilterBar';
import { InvoiceQuickEdit } from '@/components/invoices/InvoiceNumberLink';
import { CONVERSION_APPROXIMATE_NOTE } from '@/lib/targets';
import {
  DEFAULT_REVENUE_CURRENCY,
  REVENUE_CURRENCIES,
  useRevenueAnalytics,
  type RevenueCurrency,
} from '@/lib/use-revenue-analytics';
import {
  ALL,
  DEFAULT_COMPANY_SORT,
  REVENUE_COMPANY_SORTS,
  currentCycleOf,
  cyclesInScope,
  excludedCompanyCount,
  floorRows,
  keyListParam,
  matchesSearch,
  orderRows,
  parseCompanySort,
  parseFloor,
  parseKeyList,
  productOptionsOf,
  regionKeyOf,
  regionOptionsOf,
  rowsForProduct,
  scopeRows,
  totalOf,
  todaysCycleOf,
  tradedIn,
} from './revenue-company-shared';
import { usePageLayout } from '@/lib/use-page-layout';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { RevenueHeadline } from './RevenueHeadline';
import { RevenueCompanyTable } from './RevenueCompanyTable';

/**
 * THE **Revenue by Company** TAB OF THE MANAGEMENT DASHBOARD.
 *
 * *"How much all the company pays, which products/projects they are in, the
 * total amount company-wise cycle-wise"* — owner, 11 September 2026. Three
 * layers, largest question first:
 *
 *   the filter bar      which currency and cycle, then who is in the population
 *   four headline tiles how much, from how many, how typical, how concentrated
 *   the ranked table    who paid, what share that is, and what they bought
 *
 * 🔴 **THE "Revenue by cycle" CARD WAS REMOVED FROM THIS TAB — owner's
 * instruction, 13 September 2026.** *SUPERSEDED, a fourth layer between the
 * tiles and the table: "the cycle strip — what each cycle contributed, and the
 * cycle picker".*
 *
 * ⚠️ **`RevenueCycleStrip` STILL EXISTS AND IS STILL MOUNTED — by the Revenue
 * by PRODUCT tab, which imports it out of this folder.** It was not deleted and
 * must not be: it is not an orphan, it has a second caller, and removing it
 * would take the cycle strip off a tab nobody asked to change. **Do not "finish
 * the job" by deleting it**, and do not read its presence here as a card this
 * tab forgot to render.
 *
 * ⚠️ **THE CYCLE FILTER SURVIVED THE CARD.** The strip was also the cycle
 * PICKER — pressing a bar set `rcCycle` — so removing it would have orphaned
 * that state had the `Combobox` in the bar not set the same key. It does, and it
 * is now the ONLY control for it, which is why that control must stay primary
 * and visible rather than moving into the advanced half.
 *
 * ⚠️ **NO `PageHeader` AND NO PERMISSION CHECK.** `ManagementDashboardPage`
 * owns the title, the tab strip and the gate; this component is the panel it
 * mounts. It takes no props and owns its own data, so the page has nothing to
 * wire and nothing to keep in step.
 *
 * ── 🔴 THE CURRENCY CONVERTS. IT IS A BASE, NOT A FILTER — owner, 2026-09-18 ─
 *
 * ⚠️ *SUPERSEDED: "**ONE CURRENCY AT A TIME. IT IS THE DESIGN, NOT A FILTER.**
 * This tab adds no two currencies, by decision … **this tab was deliberately
 * left filtering, and the conversion must not be carried across** … the currency
 * is chosen FIRST and everything on screen is in it … **Switching currency HIDES
 * COMPANIES**, and a shorter list is indistinguishable from a quiet year. The
 * count of companies that took none of the chosen currency is stated under the
 * control that causes it."*
 *
 * **It was carried across, on the owner's instruction.** `GET /revenue-tracker`
 * takes a `baseCurrency` and prices every paid line at **its own invoice's
 * `bill_date`** out of `f_exchange_rates` (backend migration 178), so the control
 * asks *"show me the business in pounds"* rather than *"show me the pound part of
 * the business"*. ⛔ **The Google Sheet mirror still FILTERS**; that surface is
 * the backend's own.
 *
 * Three consequences, each handled where it arises:
 *
 * 1. **EVERY COMPANY IS LISTED, so the "N companies took no X and are not
 *    listed" line is GONE rather than re-worded.** There is no such population
 *    left to report: the sentence measured 24 of 124 under USD and most of the
 *    estate under GBP, and a sentence about a population that cannot exist is
 *    worse than none.
 * 2. **The "Also paid in EUR" badge withdraws itself**, because the sibling
 *    figure it pointed at has been summed into the one beside it. It follows the
 *    DATA rather than a flag — `currenciesUsedBy` answers `[base]` on a converted
 *    response — so it returns of its own accord against an older server, which is
 *    exactly when it is still needed.
 * 3. **Nothing here may be captioned "all revenue"** — unchanged, and for a new
 *    reason as well as the old one: a converted total is a fair restatement
 *    rather than the figure the accounts will show, which is what the conversion
 *    disclaimer under the tiles says.
 *
 * 🔴 **THE UNIT IS THE SERVER'S ECHO, NEVER THE PICKER'S VALUE** — see
 * `figuresCurrency` below. An API predating `baseCurrency` ignores it and answers
 * an ordinary `200` of unconverted rows, and labelling those `GBP` is the one way
 * this change fails: silently, on money, with no error to find.
 *
 * 🔴 **WHICH IS WHY THE CURRENCY AND THE CYCLE ARE THE BAR'S *PRIMARY* HALF.**
 * `DashboardFilterBar`'s own rule: a control that changes the UNIT of a figure
 * is never behind a disclosure, because a reader who cannot see which currency
 * they are looking at is a reader who will quote the wrong number. Everything
 * else here narrows a population already defined and lives in the advanced
 * half, where the bar's count keeps it from being a silent filter.
 *
 * ── ⚠️ WHAT THESE FIGURES ARE ─────────────────────────────────────────────
 *
 * **Paid invoices only, cash collected INCLUDING TAX** — owner's decision,
 * 2026-10-16 (⚠️ *SUPERSEDED: "EX-TAX — the server sums `line_subtotal`"*).
 * Each line's figure is `paidFigureOf` (`amountInclTax`) in
 * `@/lib/use-revenue-analytics`. Never worded "invoiced" or "billed": an
 * invoice nobody has paid is in none of these numbers.
 *
 * ── ⚠️ FILTERING IS IN THE BROWSER; THE CURRENCY IS NOT ────────────────────
 *
 * `useRevenueAnalytics` fetches every page of `GET /revenue-tracker` once — 122
 * companies over two requests — and every NARROWING control here works on what
 * came back. ⚠️ *SUPERSEDED 2026-09-18: "No filter adds a query parameter."*
 * **The base currency does**, and it is not a filter: it narrows no rows and
 * decides the unit, which is arithmetic only the server can do because only it
 * holds the rates and the per-line bill dates. **Every other control is still
 * browser-side**, and a guessed key would still be a 422 on the only read this
 * tab has.
 */

/* -------------------------------------------------------------------------
 * The URL
 * --------------------------------------------------------------------- */

/**
 * The query keys this tab owns.
 *
 * ⚠️ **PREFIXED, BECAUSE THE PAGE AND ITS SIBLING TABS SHARE ONE QUERY
 * STRING.** `ManagementDashboardPage` writes `?tab=`, and the other three views
 * carry filters of their own; unprefixed `cycle` or `q` would be one tab
 * silently reading another's state. The prefix is the whole guard, so a new key
 * here takes it too.
 *
 * ⚠️ **EVERY CONTROL IS IN THE URL, INCLUDING THE ADVANCED ONES**, which is
 * what makes a narrowed view shareable — and is also why the bar's active
 * count matters: a pasted link can arrive with three filters already in force
 * and the reader has to be told before they read the figure.
 */
const PARAM = {
  currency: 'rcCurrency',
  cycle: 'rcCycle',
  region: 'rcRegion',
  product: 'rcProduct',
  search: 'rcQ',
  min: 'rcMin',
  sort: 'rcSort',
  trading: 'rcTrading',
} as const;

/** The advanced half's keys, so "clear" and "count" cannot disagree about the set. */
const ADVANCED_PARAMS = [
  PARAM.region,
  PARAM.product,
  PARAM.search,
  PARAM.min,
  PARAM.sort,
  PARAM.trading,
] as const;

/** The `rcTrading` value that means "only companies still trading". */
const TRADING_CURRENT = 'current';

/**
 * ⚠️ **UNKNOWN INPUT FALLS BACK RATHER THAN THROWING OR RENDERING NOTHING.**
 * These land in a URL that gets typed, pasted and truncated by chat clients,
 * and a dashboard that answers a mangled address with a blank page is a support
 * report. An unrecognised currency is simply not one of the three.
 */
function parseCurrency(raw: string | null): RevenueCurrency {
  return raw !== null && (REVENUE_CURRENCIES as readonly string[]).includes(raw)
    ? (raw as RevenueCurrency)
    : DEFAULT_REVENUE_CURRENCY;
}

/**
 * The currency control's options.
 *
 * ⚠️ **DERIVED FROM `REVENUE_CURRENCIES`, never written out again.** A second
 * list offers exactly the currencies somebody remembered on the day, so a
 * fourth added to the library and not here becomes unreachable while its
 * figures are perfectly computable.
 */
const CURRENCY_OPTIONS: readonly SegmentedControlOption<RevenueCurrency>[] =
  REVENUE_CURRENCIES.map((code) => ({ value: code, label: code }));

/** One control's width in the bar, so seven of them line up rather than ragging. */
const SLOT = 'w-full sm:w-56';

export function RevenueByCompanyPanel(): ReactElement {
  /*
   * Layout manager (owner 2026-10-20): the headline and the table are movable
   * per person; the filter bar and the notices stay fixed above them. The ids
   * are stored per user — never rename them. Called before the early return
   * below, so the hook order never changes.
   */
  const layout = usePageLayout('management-dashboard.revenue-company', [
    { id: 'summary', label: 'Summary' },
    { id: 'companies', label: 'Revenue by company' },
  ]);
  /*
   * ⚠️ `useSearchParams()` with NO argument. Its first parameter is a DEFAULT
   * INIT rather than options — `useSearchParams({ replace: true })` injects a
   * literal `replace=true` into the query string and then into everything
   * parsed out of it. The `{ replace: true }` belongs on the setter.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const params = useMemo(() => new URLSearchParams(searchParamsKey), [searchParamsKey]);

  const currency = parseCurrency(params.get(PARAM.currency));

  /*
   * 🔴 **THE CURRENCY IS A REQUEST PARAMETER NOW, so the read is keyed on it.**
   * It converts rather than narrowing, and the arithmetic belongs to the server:
   * only it holds the rates and each line's own bill date.
   */
  const { rows, cycles, loading, error, unmatchedLines, convertedTo, unpricedLines, reload } =
    useRevenueAnalytics(currency);

  /*
   * 🔴 **THE UNIT EVERY FIGURE ON THIS TAB IS IN, RESOLVED ONCE AND READ FROM
   * THE SERVER'S ECHO.** This is the single line that stands between the tab and
   * its one silent failure: an API predating `baseCurrency` ignores the key,
   * answers a perfectly ordinary `200` of UNCONVERTED rows, and a panel labelling
   * those from `currency` would print `GBP` over dollars on a tab whose figures a
   * finance reader adds up.
   *
   * ⚠️ **Falling back to `currency` restores the OLD behaviour exactly**, filter
   * and all — `revenueFor` then admits only lines in the picked currency, which
   * is what this tab did up to 2026-09-18. That is the honest degradation: fewer
   * companies, correctly labelled, rather than more companies wrongly labelled.
   *
   * ⚠️ **NARROWED TO A CURRENCY THIS TAB CAN RENDER, and an unrecognised echo is
   * treated as UNCONVERTED rather than coerced.** Every render prop here takes
   * {@link RevenueCurrency}, and both ways out of that are worse: casting the
   * string through puts a code on screen the page cannot format, and falling back
   * to the picker's value LABELS the response with a unit it is not in — the one
   * failure the echo exists to prevent, arriving through a type assertion instead
   * of through a request. Unreachable against a conforming server, which can only
   * echo a code it was sent.
   */
  const measuredIn =
    convertedTo !== null && (REVENUE_CURRENCIES as readonly string[]).includes(convertedTo)
      ? (convertedTo as RevenueCurrency)
      : null;
  const converted = measuredIn !== null;
  const figuresCurrency: RevenueCurrency = measuredIn ?? currency;

  /* Several bands and several products (owner 2026-10-28), CSV in the URL; `[]` = all. */
  const regionKeysCsv = params.get(PARAM.region) ?? ALL;
  const regionKeys = useMemo(() => parseKeyList(regionKeysCsv), [regionKeysCsv]);
  /*
   * ⚠️ **RESOLVED LATER, AGAINST WHETHER THE SERVER REPORTS PRODUCTS AT ALL** —
   * see `productKeys` below. Read raw here only because the options it is
   * resolved against are derived from rows this component has not seen yet.
   */
  const requestedProductsCsv = params.get(PARAM.product) ?? ALL;
  const search = params.get(PARAM.search) ?? '';
  const requestedCycle = params.get(PARAM.cycle) ?? ALL;
  const minRaw = params.get(PARAM.min) ?? '';
  const sort = parseCompanySort(params.get(PARAM.sort));

  /*
   * ⚠️ **THE FLOOR IS THE PARSED NUMBER, NOT THE TEXT.** A box holding `abc`
   * narrows nothing, so it must not count towards the bar's active badge — a
   * badge that says "1 filter" over an unfiltered table teaches a reader to
   * distrust the one signal standing between them and a hidden filter.
   */
  const floor = parseFloor(minRaw);

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParamsKey);
      // A default is spelled by the key's ABSENCE — one canonical URL per view.
      if (value === ALL) next.delete(key);
      else next.set(key, value);
      /*
       * `replace: true`: changing a filter is adjusting what is on screen, not
       * navigating, and a history entry per keystroke would make Back mean
       * "undo one filter".
       */
      setSearchParams(next, { replace: true });
    },
    [searchParamsKey, setSearchParams],
  );

  /*
   * ⚠️ **THE CYCLE IS RESOLVED AGAINST THE DATA, and an unknown one reads as
   * "all cycles" EVERYWHERE.** The cycle list is admin-maintained, so a saved
   * link can name a cycle that has since been retired. Resolving once here is
   * what stops the control sitting blank while the figures below quietly
   * cover everything — one value, so the control and the report cannot
   * disagree about what is being shown.
   *
   * ⚠️ *SUPERSEDED 2026-09-13, one word: "what stops the `<select>` sitting
   * blank".* The control is a `Combobox` now, and an unmatched value there
   * falls back to the PLACEHOLDER rather than to a blank box — which would read
   * "All cycles" over figures narrowed to nothing, i.e. worse than the blank it
   * replaced. **This resolution is therefore MORE load-bearing than when it was
   * written, not less.** The billing-region control below has no such
   * resolution and injects its held value instead; the two are the only two
   * answers, and doing neither is what produces a confident wrong label.
   */
  const cycleKey = useMemo(
    () => (cycles.some((cycle) => cycle.name === requestedCycle) ? requestedCycle : ALL),
    [cycles, requestedCycle],
  );

  const scopeCycles = useMemo(() => cyclesInScope(cycles, cycleKey), [cycles, cycleKey]);
  const cycleLabel = cycleKey === ALL ? 'All cycles' : cycleKey;

  /*
   * The cycle the business is in today, which the "still trading" control is
   * named after. `null` only when there are no cycles at all, in which case the
   * control is not offered — see `currentCycleOf`.
   */
  const currentCycle = useMemo(() => currentCycleOf(cycles), [cycles]);

  /* The cycle containing today, strictly — the column the company matrix marks. */
  const todaysCycleId = useMemo(() => todaysCycleOf(cycles)?.id ?? null, [cycles]);

  /*
   * ⚠️ **RESOLVED AGAINST THE CONTROL'S EXISTENCE, not just the URL.** With no
   * cycle to name, a stale `rcTrading=current` would narrow nothing and still
   * be counted as an active filter the reader has no control to clear it with.
   */
  const tradingOnly = params.get(PARAM.trading) === TRADING_CURRENT && currentCycle !== null;

  /* ── filtering, in the browser ───────────────────────────────────────── */

  /** Every band present in the data, so the picker cannot offer an empty one. */
  const regionOptions = useMemo(() => regionOptionsOf(rows), [rows]);

  /*
   * 🔴 **THE HELD BAND IS INJECTED BACK WHEN THE DATA DOES NOT CONTAIN IT —
   * added 2026-09-13 WITH the `Combobox`, and it is the one thing that had to
   * change beyond the control itself.**
   *
   * `regionKey` comes straight off the URL and is deliberately NOT resolved
   * against the data the way `cycleKey` above is — narrowing to a band nobody
   * traded in is a real question with a real answer (nothing), so resolving it
   * away would silently widen a shared link. The cost is that the value can sit
   * off the loaded list, and the two controls answer that differently:
   *
   *   a `<select>`  renders BLANK          — visibly odd, and it was
   *   a `Combobox`  renders its PLACEHOLDER — "All billing regions", over an
   *                                           EMPTY table and an active-filter
   *                                           badge reading 1
   *
   * ⚠️ **The second is strictly worse: it is a confident wrong label rather
   * than an obvious absence**, and it is what a straight swap would have
   * shipped. So the held key is offered as its own row instead, carrying a hint
   * that says why it matches nothing. **Never drop this when the loading state
   * or the option source changes** — while `rows` is still empty every band in
   * a pasted link is "unlisted", which is exactly when the reader most needs
   * the trigger to keep saying what it is filtered to.
   */
  const regionPickerOptions = useMemo<MultiComboboxOption[]>(() => {
    const base: MultiComboboxOption[] = regionOptions.map(({ value, label }) => ({ value, label }));
    const listed = new Set(base.map((option) => option.value));
    /*
     * The KEY is the label, because there is no row to read a label off — that
     * is the whole of what "not in the data" means here. The hint is a fact
     * about the data, not advice, so it is the kind of sentence the
     * no-helper-text rule keeps.
     */
    const held = regionKeys
      .filter((key) => !listed.has(key))
      .map((key) => ({ value: key, label: key, hint: 'No company loaded is in it' }));
    return held.length === 0 ? base : [...base, ...held];
  }, [regionOptions, regionKeys]);

  /* ── the product, and the one projection that carries it everywhere ──── */

  /**
   * Every product the loaded revenue names.
   *
   * 🔴 **EMPTY MEANS THE SERVER REPORTS NO PRODUCTS AT ALL**, which is an
   * ordinary answer from an API predating the key — this app deploys separately
   * and can be newer than it. See `productKeyOfService`.
   */
  const productOptions = useMemo(() => productOptionsOf(rows), [rows]);

  /*
   * 🔴 **FORCED BACK TO `ALL` WHEN NOTHING REPORTS A PRODUCT, AND THAT IS THE
   * GUARD RATHER THAN TIDINESS.** The control below is withheld in that state,
   * so a pasted `?rcProduct=` left in force would narrow the table to nothing
   * with **no control on screen able to clear it** — the invisible active filter
   * this app refuses everywhere, and exactly the defect the sales tracker's
   * removed `nonEmptyOnly` key was left inert to avoid.
   *
   * ⚠️ **It also means the badge cannot count a filter the reader has no way to
   * reach**, which is the same rule `tradingOnly` follows one control along.
   */
  const productKeys = useMemo(
    () => (productOptions.length > 0 ? parseKeyList(requestedProductsCsv) : []),
    [productOptions.length, requestedProductsCsv],
  );

  /*
   * ⚠️ **THE HELD PRODUCT IS INJECTED BACK WHEN THE LOADED LINES DO NOT NAME
   * IT** — the identical treatment, for the identical reason, as the billing
   * region below. A `Combobox` answers an unmatched value with its PLACEHOLDER,
   * so without this row a link naming a product nobody bought would read "All
   * products" over an empty table with the badge saying one filter is on: a
   * confident wrong label rather than a visible absence.
   */
  const productPickerOptions = useMemo<MultiComboboxOption[]>(() => {
    const base: MultiComboboxOption[] = productOptions.map(({ value, label }) => ({ value, label }));
    const listed = new Set(base.map((option) => option.value));
    /*
     * The KEY is the label, because there is no line to read a name off — which
     * is the whole of what "not in the loaded revenue" means here.
     */
    const held = productKeys
      .filter((key) => !listed.has(key))
      .map((key) => ({ value: key, label: key, hint: 'Nothing loaded was sold on it' }));
    return held.length === 0 ? base : [...base, ...held];
  }, [productOptions, productKeys]);

  /**
   * The rows as they would be if every company had only ever bought this
   * product.
   *
   * 🔴 **ONE PROJECTION, ABOVE EVERY OTHER FILTER AND EVERY FIGURE.** See
   * `rowsForProduct`: the whole tab derives its money from a row's `services`,
   * so narrowing them once here moves the table, the four tiles, the total, the
   * median, the concentration, the floor and "still trading" together — and no
   * two of them can end up describing different populations under one heading.
   */
  const productRows = useMemo(() => rowsForProduct(rows, productKeys), [rows, productKeys]);

  /**
   * What every caption on this tab says the figures cover.
   *
   * 🔴 **IT NAMES THE PRODUCT, AND THAT IS NOT DECORATION.** Both the headline
   * tiles and the table render `Paid, incl. tax · {currency} · {scopeLabel}`, so a
   * product-narrowed figure under an unchanged caption would be a heading
   * claiming all revenue over a fraction of it — the one thing this tab's own
   * rules forbid on a tile, a heading or a total.
   */
  const scopeLabel = useMemo(() => {
    if (productKeys.length === 0) return cycleLabel;
    const labels = productKeys.map(
      (key) => productOptions.find((option) => option.value === key)?.label ?? key,
    );
    return `${cycleLabel} · ${labels.length <= 2 ? labels.join(', ') : `${labels.length} products`}`;
  }, [cycleLabel, productKeys, productOptions]);

  /*
   * The band, the search and the trading filter narrow WHICH COMPANIES are in
   * play; the cycle and the currency narrow WHICH OF THEIR MONEY counts; the
   * product has already narrowed both, above.
   */
  /* ⚠️ **`figuresCurrency`, NEVER `currency`, from here down.** Everything below
   * reaches the wire through `revenueFor`, whose parameter is the unit the
   * FIGURES are in — the server's echo where it converted, the picker's value
   * where it did not. Passing the request instead is the one mistake that
   * mislabels money, and it typechecks. */
  const companyRows = useMemo(() => {
    const wantedRegions = new Set(regionKeys);
    return productRows.filter(
      (row) =>
        (wantedRegions.size === 0 || wantedRegions.has(regionKeyOf(row))) &&
        matchesSearch(row, search) &&
        (!tradingOnly || currentCycle === null || tradedIn(row, currentCycle, figuresCurrency)),
    );
  }, [productRows, regionKeys, search, tradingOnly, currentCycle, figuresCurrency]);

  /*
   * 🔴 **THE FLOOR IS APPLIED ONCE, HERE, ABOVE EVERYTHING THAT READS A
   * FIGURE — the headline tiles as well as the table.** Applied at the table
   * alone, the tiles would count money from accounts the table has hidden and
   * the two would disagree under one heading, with nothing on screen to explain
   * the gap. With no floor set — the ordinary case — `floorRows` returns its
   * input untouched and every figure is what it was before this control existed.
   *
   * ⚠️ *SUPERSEDED 2026-09-13, the reason and not the rule: "applied before the
   * CYCLE STRIP is built, not only to the table … both panels then describe the
   * same companies".* That card is gone from this tab, so the strip is no longer
   * one of the readers — **the tiles still are, so this stays exactly where it
   * is.** Do not fold it into the table's own memo on the strength of the strip
   * having left; that would reintroduce the disagreement one consumer down.
   */
  const flooredRows = useMemo(
    () => floorRows(companyRows, scopeCycles, figuresCurrency, floor),
    [companyRows, scopeCycles, figuresCurrency, floor],
  );

  /*
   * ⚠️ **RANKED BY REVENUE, ALWAYS.** This is the array the headline tiles read
   * concentration and the median off, and `topShareOf` means *the ten largest*
   * by taking its first ten. The reader's ordering is applied to a COPY below
   * and reaches the table alone.
   */
  const scoped = useMemo(
    () => scopeRows(flooredRows, scopeCycles, figuresCurrency),
    [flooredRows, scopeCycles, figuresCurrency],
  );

  const ordered = useMemo(() => orderRows(scoped, sort), [scoped, sort]);

  const total = useMemo(() => totalOf(scoped), [scoped]);

  /*
   * ⚠️ Taken over EVERY row and EVERY cycle, deliberately — it answers "who is
   * missing because of the CURRENCY", which used to be the one control whose
   * effect a reader could not see. See `excludedCompanyCount`.
   *
   * 🔴 **SHORT-CIRCUITED UNDER CONVERSION RATHER THAN LEFT TO READ `0`.** With
   * every currency in the figures there is no such population, so this would
   * answer `0` of its own accord on a healthy response — but not for certain: a
   * company whose every line carries an UNSTATED unit (`d_invoices.currency` is
   * nullable) is admitted by no gate in any mode and would still be counted here,
   * then reported as *"took no GBP"*, which names a currency filter that no
   * longer exists over a company whose real problem is an invoice with no
   * currency on it. Computing it at all in that state is what the early exit
   * removes.
   */
  const excluded = useMemo(
    () => (converted ? 0 : excludedCompanyCount(rows, cycles, figuresCurrency)),
    [converted, rows, cycles, figuresCurrency],
  );

  /*
   * 🔴 **THE BAR'S ACTIVE COUNT: ADVANCED CONTROLS THAT DIFFER FROM THEIR
   * DEFAULT, AND NOTHING ELSE.** That is `DashboardFilterBar`'s contract and it
   * is the whole reason a collapsed panel is safe — the count is what stops a
   * hidden filter being a silent one.
   *
   * ⚠️ **THE CURRENCY AND THE CYCLE ARE NOT COUNTED.** They are the primary
   * half, visible without opening anything, so counting them would inflate a
   * badge whose job is to report what the reader CANNOT see.
   *
   * ⚠️ **The floor counts only when it PARSES** — see `floor` above.
   */
  const activeAdvanced =
    (search.trim() !== '' ? 1 : 0) +
    (regionKeys.length > 0 ? 1 : 0) +
    /* Already forced to `[]` where the control is withheld — see `productKeys`. */
    (productKeys.length > 0 ? 1 : 0) +
    (floor !== null ? 1 : 0) +
    (sort !== DEFAULT_COMPANY_SORT ? 1 : 0) +
    (tradingOnly ? 1 : 0);

  /**
   * Is anything narrowing what is on screen — including the cycle?
   *
   * ⚠️ **WIDER THAN `activeAdvanced` ON PURPOSE, and the two are not
   * interchangeable.** The badge reports what is HIDDEN from the reader; this
   * decides whether an empty table should say *"no company matches these
   * filters"* or *"no revenue in this currency"*, and a cycle with nothing in
   * it is the commonest cause of the first.
   */
  const narrowed = activeAdvanced > 0 || cycleKey !== ALL;

  /** Nothing has arrived yet — the only state that earns a skeleton. */
  const awaitingFirstRead = loading && rows.length === 0;

  /**
   * Resets the advanced half, which is what the bar's own Clear promises.
   *
   * ⚠️ **THE CYCLE SURVIVES IT, and the currency is not resettable at all.**
   * There is no "all currencies" state and there cannot be one — that is the
   * whole design — so clearing to it would be clearing to something
   * unrenderable.
   */
  const clearAdvanced = useCallback(() => {
    const next = new URLSearchParams(searchParamsKey);
    for (const key of ADVANCED_PARAMS) next.delete(key);
    setSearchParams(next, { replace: true });
  }, [searchParamsKey, setSearchParams]);

  /**
   * Resets everything that narrows, cycle included.
   *
   * ⚠️ **THE EMPTY TABLE'S ACTION, AND DELIBERATELY NOT THE BAR'S.** A reader
   * looking at an empty table wants every company back, and a button that left
   * the cycle in place would frequently leave them looking at the same empty
   * table — a control that visibly does nothing, which is worse than no
   * control. The bar's Clear keeps to its documented contract.
   */
  const clearAll = useCallback(() => {
    const next = new URLSearchParams(searchParamsKey);
    for (const key of ADVANCED_PARAMS) next.delete(key);
    next.delete(PARAM.cycle);
    setSearchParams(next, { replace: true });
  }, [searchParamsKey, setSearchParams]);

  /* ── the read failed, and there is nothing behind it ─────────────────── */

  /*
   * ⚠️ **A FAILED READ WITH NOTHING ON SCREEN MUST NOT FALL THROUGH TO THE
   * TABLE'S EMPTY STATE.** That state says "no revenue in this currency", which
   * is a statement about the business; this is a statement about the request.
   * Reading one as the other is how a broken endpoint gets reported as a quiet
   * quarter. A failed REFRESH over figures already on screen is a different
   * matter — those stay, with a strip above them saying they are stale.
   */
  if (error !== null && rows.length === 0) {
    return (
      <Panel className="min-w-0">
        <EmptyState
          title="The revenue figures could not be loaded"
          description={error}
          action={
            <Button variant="secondary" onClick={reload}>
              Try again
            </Button>
          }
        />
      </Panel>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <DashboardFilterBar
        rememberAs="revenue-company-more-filters"
        activeAdvanced={activeAdvanced}
        onClear={clearAdvanced}
        primary={
          <>
            {/*
             * 🔴 **THE CURRENCY IS THE UNIT OF EVERY FIGURE ON THIS TAB, so it
             * is a segmented control rather than a `<select>`** — the three
             * options are visible at once, and the one in force is readable
             * without opening anything.
             *
             * `FilterSlot` rather than `Field`: a segmented control renders a
             * GROUP of buttons with no single thing for a `<label for>` to
             * point at, and the group carries its own `aria-label` saying the
             * same word as the visible one.
             */}
            <FilterSlot label="Currency">
              <SegmentedControl
                label="Currency"
                value={currency}
                options={CURRENCY_OPTIONS}
                onChange={(next) => setParam(PARAM.currency, next)}
              />
            </FilterSlot>

            {/*
             * ⚠️ **PRIMARY, BESIDE THE CURRENCY.** The cycle decides the PERIOD
             * every figure covers, which is half of what a figure means.
             *
             * 🔴 **AND SINCE THE "Revenue by cycle" CARD WENT (2026-09-13) IT IS
             * THE ONLY CONTROL FOR `rcCycle` AT ALL**, where it used to be one of
             * two. *SUPERSEDED: "the cycle strip below is the same piece of
             * state, so a reader who pressed a bar there must be able to see what
             * they selected without opening a panel."* **Moving it into the
             * advanced half would now put the period every figure covers behind a
             * disclosure** — and a pasted `?rcCycle=` link would arrive with no
             * visible control naming it. It stays primary.
             */}
            {/*
             * ✅ **ALL FOUR DROPDOWNS ON THIS TAB ARE SEARCHABLE `Combobox`es —
             * owner's decision, 2026-09-13.** *SUPERSEDED: four `ui/Select`s
             * with inline `<option>` lists.* Every non-table-column filter
             * dropdown in the app takes one shape, and the owner chose that
             * consistency explicitly over the "a handful of options needs no
             * search box" reading. The cycle list is the one that also earns it
             * on its own — eight cycles today and one more every year.
             *
             * ⛔ **THE CURRENCY ABOVE IS NOT ONE OF THEM AND MUST NOT BECOME
             * ONE.** It is a `SegmentedControl` because the unit in force has to
             * be readable without opening anything — see the comment on it.
             *
             * ⚠️ **`ALL` is `''`, which is what lets "All cycles" be
             * `emptyOptionLabel`.** The same string is also the `placeholder`,
             * and that is not belt-and-braces: `Combobox` looks the trigger's
             * label up in `options`, which the empty row is NOT part of, so
             * without the placeholder an unfiltered control would read
             * "Select…". The reference is `DealsPage`'s four filters.
             */}
            <div className={SLOT}>
              <Field label="Cycle">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Combobox
                    id={id}
                    aria-describedby={describedBy}
                    value={cycleKey}
                    onChange={(value) => setParam(PARAM.cycle, value)}
                    options={cycles.map((cycle) => ({ value: cycle.name, label: cycle.name }))}
                    emptyOptionLabel="All cycles"
                    placeholder="All cycles"
                    searchPlaceholder="Search cycles…"
                  />
                )}
              </Field>
            </div>

            {/*
             * `order-last` puts the layout toolbar after the bar's own Filters
             * disclosure, which carries the row's `ml-auto`, so both sit at the
             * row's end.
             */}
            <div className="order-last flex items-end">
              <LayoutToolbar layout={layout} />
            </div>
          </>
        }
        advanced={
          <>
            {/* The company search lives in the table's own header, beside Columns (owner 2026-10-27). */}
            {/*
             * ⚠️ **`regionPickerOptions`, NOT `regionOptions` — see the memo.**
             * The held band is injected when the loaded rows do not contain it,
             * because this value comes off the URL unresolved and a `Combobox`
             * answers an unmatched value with its placeholder. "All billing
             * regions" printed over an empty table is the one outcome worse
             * than the blank box this replaced.
             *
             * ⛔ **NO `loading` PROP ON ANY DROPDOWN IN THIS BAR, deliberately.**
             * `Combobox` renders "Searching…" INSTEAD OF the list while it is
             * set, which here would hide the "All …" row — the only row that is
             * always correct and the one a reader arriving on a filtered link
             * needs in order to clear it. These lists are derived from rows
             * already in the browser, not fetched per control.
             */}
            <div className={SLOT}>
              <Field label="Billing region">
                {({ id, 'aria-describedby': describedBy }) => (
                  <MultiCombobox
                    id={id}
                    aria-describedby={describedBy}
                    values={regionKeys}
                    onChange={(values) => setParam(PARAM.region, keyListParam(values))}
                    options={regionPickerOptions}
                    placeholder="All billing regions"
                    searchPlaceholder="Search billing regions…"
                    summaryNoun={{ singular: 'billing region', plural: 'billing regions' }}
                  />
                )}
              </Field>
            </div>

            {/*
             * 🔴 **THE PRODUCT NARROWS THE FIGURES, NOT ONLY THE LIST — owner's
             * decision, 2026-09-14.** With one chosen, every amount on this tab
             * is that product's revenue for that company, and `scopeLabel`
             * names it on the tiles and the table so no caption claims more
             * than it shows. See `rowsForProduct`.
             *
             * ⛔ **IT MATCHES THE PRODUCT ID, NEVER `serviceName`.** That field
             * is the PROJECT's name wherever a line names a project, so one
             * product sold bare and sold through a project is two different
             * strings — filtering on it would split a product across buckets
             * and under-report it with nothing on screen to show it. The
             * `product` key on each paid line is what this reads.
             *
             * ⚠️ **WITHHELD ENTIRELY WHEN THE SERVER REPORTS NO PRODUCTS**, an
             * ordinary answer from an API predating the key rather than a
             * fault — and `productKey` is forced back to `ALL` with it, so no
             * filter can be in force without a control to clear it. The
             * neighbouring Trading control is withheld the same way when there
             * is no cycle to name.
             */}
            {productOptions.length > 0 && (
              <div className={SLOT}>
                <Field label="Product">
                  {({ id, 'aria-describedby': describedBy }) => (
                    <MultiCombobox
                      id={id}
                      aria-describedby={describedBy}
                      values={productKeys}
                      onChange={(values) => setParam(PARAM.product, keyListParam(values))}
                      options={productPickerOptions}
                      placeholder="All products"
                      searchPlaceholder="Search products…"
                      summaryNoun={{ singular: 'product', plural: 'products' }}
                    />
                  )}
                </Field>
              </div>
            )}

            {/*
             * ⚠️ **THE LABEL AND THE HINT ARE WORDED TO MATCH TOP MOVERS'
             * FLOOR**, which asks the same question on the neighbouring tab. A
             * second phrasing of one idea is how two tabs come to look like two
             * products; the CODE behind it is deliberately not shared, because
             * the rule differs — see `parseFloor`.
             */}
            {/*
             * ⚠️ **THE LABEL NAMES `figuresCurrency`, NOT THE PICKER.** The
             * threshold is compared against the amounts on screen, which are in
             * the unit the SERVER measured in — so a label taken from the request
             * would put a box reading "GBP" over a comparison against dollars on
             * any server predating `baseCurrency`.
             */}
            <div className="w-full sm:w-48">
              <Field
                label={`Minimum revenue (${figuresCurrency})`}
                hint="Hides a company whose revenue in this scope is below it."
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step={1000}
                    placeholder="0"
                    aria-describedby={describedBy}
                    value={minRaw}
                    onChange={(event) => setParam(PARAM.min, event.target.value)}
                  />
                )}
              </Field>
            </div>

            {/*
             * ⚠️ **THE OPTION NAMES THE CYCLE**, because "still trading" is a
             * claim about a period and a reader has to be able to see which
             * one. It is a different question from the cycle picker above: that
             * changes the FIGURES to one cycle, this keeps the figures as they
             * are and narrows the POPULATION to accounts that are still active.
             *
             * Not rendered at all when there is no cycle to name — an option
             * reading "Active in" with nothing after it is a rendering fault.
             */}
            {currentCycle !== null && (
              <div className={SLOT}>
                <Field label="Trading">
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Combobox
                      id={id}
                      aria-describedby={describedBy}
                      /*
                       * ⚠️ **Already resolved to one of the two rows**
                       * (`tradingOnly` folds in `currentCycle !== null`), so
                       * this can never fall through to the placeholder — which
                       * is why the two-row list needs no injection where the
                       * band above does.
                       */
                      value={tradingOnly ? TRADING_CURRENT : ALL}
                      onChange={(value) => setParam(PARAM.trading, value)}
                      options={[{ value: TRADING_CURRENT, label: `Paid in ${currentCycle.name}` }]}
                      emptyOptionLabel="All companies"
                      placeholder="All companies"
                    />
                  )}
                </Field>
              </div>
            )}

            {/*
             * ⚠️ **ORDERING IS A FILTER-BAR CONTROL RATHER THAN A CLICKABLE
             * TABLE HEADING**, so it sits with the other narrowing controls and
             * is counted by the badge like them. A heading somebody pressed by
             * accident is an ordering nothing on screen reports.
             */}
            <div className="w-full sm:w-64">
              <Field label="Order by">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Combobox
                    id={id}
                    aria-describedby={describedBy}
                    /*
                     * ⚠️ **`parseCompanySort` has already folded an unknown
                     * ordering back to the default**, so this is always one of
                     * the three rows and the placeholder is unreachable. That
                     * parser is doing for the ordering exactly what `cycleKey`
                     * does for the cycle — and what the billing region above
                     * deliberately does not.
                     *
                     * ⛔ **NO `emptyOptionLabel`: there is no "unordered".** A
                     * table is always in some order, so an empty row would be a
                     * second spelling of the default rather than a choice.
                     */
                    value={sort}
                    onChange={(value) => setParam(PARAM.sort, value)}
                    options={REVENUE_COMPANY_SORTS.map(({ value, label }) => ({ value, label }))}
                    searchPlaceholder="Search orderings…"
                  />
                )}
              </Field>
            </div>
          </>
        }
        /*
         * ⚠️ **PHRASING CONTENT ONLY.** The bar renders this inside a `<p>`, so
         * a `<div>` or a nested `<p>` here is invalid markup that browsers
         * silently restructure.
         */
        note={
          <>
            {/*
             * 🔴 **THE "N COMPANIES TOOK NO X AND ARE NOT LISTED" SENTENCE IS
             * GONE UNDER CONVERSION, AND IT IS NOT RE-WORDED.**
             *
             * ⚠️ *SUPERSEDED, and it was true of a FILTER: "**SWITCHING CURRENCY
             * HIDES COMPANIES, AND THIS IS THE ONLY THING THAT SAYS SO.**
             * Measured on production: 652 USD invoices against 89 EUR and 56 GBP,
             * so a currency that is not USD legitimately drops most of the
             * estate. Without this line a short list reads as a quiet year."*
             *
             * **There is no such population any more.** The control converts, so
             * every company with any paid revenue is listed whatever it was
             * billed in — and a sentence reporting a shortfall that cannot occur
             * is worse than none: it teaches a reader that this tab hides
             * companies, on the one tab that has stopped doing so.
             *
             * ⚠️ **IT IS KEPT FOR THE UNCONVERTED PATH RATHER THAN DELETED, and
             * that is the whole reason `excluded` still exists.** A server
             * predating `baseCurrency` really does filter, really does drop most
             * of the estate, and really does need this line — and it is the one
             * response on which nothing else on screen would say so. Gated on the
             * echo, so it withdraws exactly when the filtering does.
             */}
            {!awaitingFirstRead && excluded > 0 && (
              <span className="text-fg-secondary">
                {excluded.toLocaleString()} of {rows.length.toLocaleString()} companies took no{' '}
                {figuresCurrency} and are not listed.{' '}
              </span>
            )}
            {/*
             * ⚠️ **THE SENTENCE ABOUT WHERE THE CONTROLS RUN HAD TO MOVE WITH
             * THE CURRENCY.** *"These controls narrow companies already loaded"*
             * was true of all of them and is now false of one: the currency costs
             * a request and converts server-side, and a reader who assumes it is
             * instant will misread a table that has not caught up.
             */}
            Every figure is paid invoices only, including tax.
          </>
        }
      />

      {/*
       * 🔴 **THE CYCLES DO NOT SUM TO THE BUSINESS'S REVENUE WHEN THIS IS ABOVE
       * ZERO.** These are paid lines the server could not place in any cycle —
       * an absent or out-of-range bill date — so they are in no column anywhere
       * on this tab. It was `0` on production on 11 September 2026; that is a
       * reading, not a property.
       *
       * ⚠️ **`warning`, NOT `danger`.** Nothing failed and nothing on screen is
       * wrong; a red band would send somebody looking for a broken page instead
       * of for an invoice dated outside every cycle window.
       *
       * ⚠️ **ITS OWN ELEMENT RATHER THAN A STRIP INSIDE THE FILTER BAR.** It is
       * a statement about the DATA, not about the controls, and the bar it used
       * to live in is now shared with three other tabs — a warning band folded
       * into a control panel is one the next reader of that panel has to decide
       * whether to keep.
       */}
      {unmatchedLines > 0 && (
        <Notice
          tone="warning"
          role="status"
          title={
            <>
              {unmatchedLines.toLocaleString()} paid{' '}
              {unmatchedLines === 1 ? 'line falls' : 'lines fall'} outside every cycle.
            </>
          }
        >
          They are in none of the figures below.
        </Notice>
      )}

      {/*
       * 🔴 **THE ONE THING THAT KEEPS CONVERSION HONEST.** A paid line whose own
       * bill date has no stored exchange rate is left OUT of every figure rather
       * than priced at par: converting at 1.0 would put dollars into a sterling
       * total as though the two were the same money. Without this sentence the
       * tab silently under-reports and looks complete.
       *
       * ⛔ **"COULD NOT BE CONVERTED", NEVER "IN ANOTHER CURRENCY", AND THE TWO
       * SEND A READER TO OPPOSITE PLACES.** A foreign line IS converted now, so
       * the old wording names a policy that no longer exists and tells somebody
       * nothing can be done. This names **missing reference data**, which a person
       * closes by backfilling the rate for that date — hence the closing clause,
       * which is the whole actionable half. The same correction
       * `excludedByCurrency` on `/targets/actuals` had to be made.
       *
       * ⚠️ **A COUNT RATHER THAN AN AMOUNT, and the amount could not be on the
       * wire**: a line that cannot be priced has no figure in this unit to state.
       * A count is the honest answer and it is what somebody takes to the rate
       * table.
       *
       * ⚠️ **`null` RENDERS NOTHING AND SO DOES `0`, FOR DIFFERENT REASONS** —
       * nothing was examined, against nothing was lost. Tested explicitly rather
       * than through truthiness, so the distinction survives in the code even
       * though it does not survive on the screen.
       *
       * ⚠️ **IT IS `0` ON PRODUCTION TODAY**, so this branch is unreachable
       * without a gap in the rate table — which is exactly why it is built and
       * rendered rather than assumed away. A figure that is only ever right is a
       * figure nobody builds a reader for, and the day a rate is missing this tab
       * would otherwise under-report with a smaller number that reads as a
       * quieter quarter.
       */}
      {unpricedLines !== null && unpricedLines > 0 && (
        <Notice
          tone="warning"
          role="status"
          title={
            <>
              {unpricedLines.toLocaleString()} paid{' '}
              {unpricedLines === 1 ? 'line could' : 'lines could'} not be converted into{' '}
              {figuresCurrency}.
            </>
          }
        >
          There is no exchange rate stored for {unpricedLines === 1 ? 'its' : 'their'} own bill
          date, so {unpricedLines === 1 ? 'it is' : 'they are'} in none of the figures below. The
          rate is missing, not the revenue.
        </Notice>
      )}

      {/*
       * ⚠️ **A FAILED REFRESH OVER FIGURES ALREADY ON SCREEN.** The numbers
       * below are stale rather than absent, and the honest render is both — they
       * stay, and this says why they are old. Swapping them for an error page
       * would throw away a screen of true-as-of-a-moment-ago answers.
       */}
      {error !== null && rows.length > 0 && (
        <Notice tone="warning" role="status" title="These figures could not be refreshed.">
          {error}
        </Notice>
      )}

      {/*
       * ⚠️ **`awaitingFirstRead`, NOT the bare `loading` flag.** `reload()` sets
       * `loading` while the rows already on screen stay exactly where they are,
       * so passing it raw would swap a screen of true-as-of-a-moment-ago
       * figures for skeletons on every refresh — the same throwing-away the
       * failed-refresh strip above exists to avoid. Skeletons are for the state
       * where there is genuinely nothing to show.
       */}
      {/* ⚠️ Every figure in these tiles came through `revenueFor` against
       * `figuresCurrency`; the code in their captions has to be the same one. */}
      <LayoutGrid
        layout={layout}
        render={{
          /*
           * The headline and its conversion footnote move together: the
           * footnote qualifies the tiles' figures, so it travels with them.
           */
          summary: () => (
            <div className="flex min-w-0 flex-col gap-4">
      <RevenueHeadline
        scoped={scoped}
        total={total}
        currency={figuresCurrency}
        scopeLabel={scopeLabel}
        loading={awaitingFirstRead}
      />

      {/*
        🔴 **HOW THESE FIGURES WERE ARRIVED AT.** Wording owned by
        `CONVERSION_APPROXIMATE_NOTE` in `@/lib/targets`; see it for the full
        reasoning. ⚠️ **That constant's own doc forbade this tab BY NAME until
        2026-09-18**, correctly, because this tab filtered and converted nothing —
        and the reversal is recorded there marked `SUPERSEDED` rather than
        deleted, so there is no contradiction left for this comment to carry.

        ⚠️ **GATED ON `converted`, WHICH READS THE SERVER'S ECHO — never on a
        currency having been picked.** Against an API predating `baseCurrency`
        nothing was converted, and a disclaimer about a conversion that did not
        happen is a false statement in the reassuring direction, which is the
        worse one.

        ⚠️ **AND ON THERE BEING FIGURES.** A disclaimer over a loading or empty
        tab qualifies nothing.

        ⚠️ **NEITHER A `warning` NOR A STRIP.** Nothing is wrong — the figures are
        correct and correctly labelled — so this takes the footnote register
        rather than the failure family, with the full statement on `title`.

        ⛔ **NOT HELPER TEXT, AND A SWEEP MUST NOT TAKE IT.** It states a fact
        about the data the reader cannot otherwise see: nothing distinguishes a
        natively-summed total from a restated one, and the wrong conclusion it
        prevents — *"this total is what the accounts will show"* — is one a
        finance reader acts on. It is attached to a FIGURE, not to a control.
      */}
      {converted && !awaitingFirstRead && rows.length > 0 && (
        <p className="m-0 text-2xs text-fg-subtle" title={CONVERSION_APPROXIMATE_NOTE.detail}>
          {CONVERSION_APPROXIMATE_NOTE.short}
        </p>
      )}
            </div>
          ),
          companies: () => (
            <>
      {/*
       * ⚠️ **ONE EDIT FORM FOR EVERY INVOICE NUMBER IN THE TABLE BELOW, mounted
       * here rather than in the row.** The detail rows can hold dozens of
       * numbers; a modal per row would be a mounted form per invoice, each with
       * its own pickers to populate. See `InvoiceQuickEdit`.
       *
       * 🔴 **`reload`, NOT a local patch.** A saved invoice changes the very
       * figures this panel ranks — every amount here is built from paid invoice
       * lines — so a currency, a status or a line edit moves the totals, the
       * shares and which companies appear at all. Leaving the ranking as it was
       * is silent: nothing errors, the numbers are simply the ones from before
       * the save.
       *
       * ⚠️ **`reload` sets `loading` while the rows stay on screen**, which is
       * why the table is given `awaitingFirstRead` and not the raw flag — see
       * the note above the headline. A refresh after a save must not blank a
       * screen of figures into skeletons.
       */}
      <InvoiceQuickEdit onSaved={reload}>
        <RevenueCompanyTable
          ordered={ordered}
          scopeCycles={scopeCycles}
          todaysCycleId={todaysCycleId}
          /* ⚠️ The unit the figures are IN — every amount in this table came
           * through `revenueFor` against this same value. */
          currency={figuresCurrency}
          /* ⚠️ Decides ONE sentence: the empty state's, which is a claim about
           * the business and is false under conversion. See the table. */
          converted={converted}
          total={total}
          scopeLabel={scopeLabel}
          loading={awaitingFirstRead}
          narrowed={narrowed}
          onClearFilters={clearAll}
          search={search}
          onSearchChange={(value) => setParam(PARAM.search, value)}
        />
      </InvoiceQuickEdit>
            </>
          ),
        }}
      />
    </div>
  );
}
